import React from 'react';
import { Controller, useFormContext, ControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useVehicleClasses } from '@/clients/vehicleClasses/useVehicleClasses.ts';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { VehicleClass } from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { SelectCombobox } from '@/shared/ui/Combobox/SelectCombobox.tsx';

type VehicleClassFieldValues = {
    [key: string]: VehicleClass;
};

export type VehicleClassesSelectionInputProps = Pick<
    ControllerProps<VehicleClassFieldValues>,
    'name' | 'defaultValue' | 'disabled'
> & {
    variant?: 'lg' | 'md';
    className?: string;
    onVehicleClassChange?: (vehicleClass?: VehicleClass) => void;
};

export const VehicleClassesSelectionInput = React.forwardRef<HTMLInputElement, VehicleClassesSelectionInputProps>(
    ({ name = 'vehicleClass', defaultValue, disabled, variant = 'lg', className, onVehicleClassChange }, _ref) => {
        const { t } = useTranslation();
        const { formatNumber } = useFormatters();

        const { control } = useFormContext();
        const { data: vehicleClasses } = useVehicleClasses();

        const options = vehicleClasses.map(vehicleClass => ({
            value: String(vehicleClass.id),
            label: t('forms.inputs.vehicleClass.options.label', {
                name: vehicleClass.name,
                payload: formatNumber(vehicleClass.payloadInTons),
            }),
        }));

        return (
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue?.id ?? null}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SelectCombobox
                        fieldRef={ref}
                        value={value?.id != null ? String(value.id) : undefined}
                        onValueChange={vehicleClassIdStr => {
                            const vehicleClass = vehicleClasses.find(vc => String(vc.id) === vehicleClassIdStr);
                            onVehicleClassChange?.(vehicleClass);
                            onChange(vehicleClass ?? null);
                        }}
                        options={options}
                        disabled={disabled}
                        placeholder={t('forms.transportOnlyForm.fields.vehicleClass.placeholder')}
                        error={fieldState.error?.message}
                        size={variant}
                        dataTest="vehicle-class-select-input"
                        className={className}
                    />
                )}
            />
        );
    }
);
